import React, { useCallback, useMemo } from 'react'

import type {
  BinaryFilter,
  Filter,
  LogicalAndFilter,
  UnaryFilter,
} from '@cubejs-client/core'
import { CountryCodeSelect } from 'cube/components/CountryCodeSelect'
import {
  type IFilterableCubeEntity,
  type OperatorSelectOption,
  CubeFilterDimensions,
  CubeOperator,
  groupOrder,
  HumanizedCubeOperators,
  isCubeFilterKeyValueType,
  isUnaryOperator,
  KeyValueFilterDict,
  MAX_MENU_HEIGHT,
  operatorHasNumericInput,
  operatorHasTextInput,
} from 'cube/components/CubeReportFilterRow'
import { HourRangeSelect } from 'cube/components/HourRangeSelect'
import { clone, equals, groupBy, path, pathOr } from 'ramda'

import {
  type SelectGroupedOptions,
  type SelectOption,
  Flex,
  IconButton,
  InputNumber,
  InputText,
  Select,
  SimpleGrid,
} from '@driftt/dds'
import { faX } from '@driftt/dds.icons/regular/faX'

import { isNilOrEmpty } from 'utils/ramda'

import { LanguageCodeSelect } from '../LanguageCodeSelect'
import { PlaybookTypeSelect } from '../PlaybookTypeSelect'
import { URLParametersSelect } from '../URLParametersSelect'
import { WordsOrPhrases } from '../WordsOrPhrases'

interface IMemberSelectProps {
  rowMainFilter: Filter
  filterableEntities: Record<string, IFilterableCubeEntity>
  filterableEntityOptions: SelectOption[] | SelectGroupedOptions[]
  onFilterChanged: (
    value: string | LogicalAndFilter,
    defaultOperator?: CubeOperator
  ) => void
}

const MemberSelect: React.FC<IMemberSelectProps> = ({
  rowMainFilter,
  filterableEntities,
  filterableEntityOptions,
  onFilterChanged,
}) => {
  const filterMember: string = pathOr('', ['member'], rowMainFilter)

  const selectedMember = !isNilOrEmpty(filterMember)
    ? filterableEntities[filterMember]?.member
    : null

  const onMemberSelectChange = useCallback(
    (option) => {
      const selectedOption = option as SelectOption<string>
      const isFilterMemberKeyValue: boolean =
        selectedOption?.value in KeyValueFilterDict
      if (isFilterMemberKeyValue) {
        const { key, value } = KeyValueFilterDict[selectedOption?.value]
        const logicalAndFilter: LogicalAndFilter = {
          and: [
            // IMPORTANT: Make sure to store the corresponding CubeFilterDimension as the first filter, since that is the logic that will be using later.
            {
              member: value,
            } as Filter,
            {
              member: key,
            } as Filter,
          ],
        }
        onFilterChanged(logicalAndFilter)
        return
      }

      const selectedFilterDimension = selectedOption?.value
      // Set a default operator for the selected filter member so users don't have to select it in the UI
      const defaultOperator = pathOr(
        undefined,
        ['availableOperations', '0'],
        filterableEntities[selectedFilterDimension]
      )
      onFilterChanged(selectedFilterDimension, defaultOperator)
    },
    [onFilterChanged, filterableEntities]
  )

  return (
    <Select
      key={`member-select-${selectedMember}`}
      label="Member select"
      hideLabel
      size="medium"
      placeholder="Select filter..."
      isClearable={false}
      options={filterableEntityOptions}
      onChange={onMemberSelectChange}
      value={selectedMember}
      rootProps={{
        id: 'cube-report-filter--member-select',
      }}
      maxMenuHeight={MAX_MENU_HEIGHT}
    />
  )
}

interface IOperationSelectProps {
  rowMainFilter: Filter
  onFilterChanged: (operator: CubeOperator) => void
  selectedMemberOperators: CubeOperator[]
  humanizedCubeOperatorOptions: SelectOption[]
  isDisabled: boolean
}

const OperationSelect: React.FC<IOperationSelectProps> = ({
  rowMainFilter,
  onFilterChanged,
  selectedMemberOperators,
  humanizedCubeOperatorOptions,
  isDisabled,
}) => {
  const operator = path(['operator'], rowMainFilter)

  const handleOnOperationSelectChange = useCallback(
    (option) => {
      const selectedOption = option as OperatorSelectOption
      onFilterChanged(selectedOption?.value)
    },
    [onFilterChanged]
  )

  return (
    <Select
      key={`operator-select-${operator}`}
      label="Operator select"
      hideLabel
      size="small"
      isClearable={false}
      value={
        humanizedCubeOperatorOptions.find(({ value }) => value === operator) ??
        null
      }
      onChange={handleOnOperationSelectChange}
      options={selectedMemberOperators.map((operator) => {
        return {
          label: HumanizedCubeOperators[operator],
          value: operator,
        }
      })}
      isDisabled={isDisabled}
      rootProps={{
        id: 'cube-report-filter--operator-select',
      }}
    />
  )
}

interface IValueSelectProps {
  filter: Filter
  rowMainFilter: Filter
  onFilterChanged: (
    values: string[],
    logicalAndFilterValues?: { [key: string]: string }
  ) => void
  selectedPropertyOptions: { label: string; value: string }[]
  isDisabled: boolean
  enforceSingleSelectValue?: boolean
}

const ValueSelect: React.FC<IValueSelectProps> = ({
  filter,
  rowMainFilter,
  onFilterChanged,
  selectedPropertyOptions,
  enforceSingleSelectValue,
  isDisabled,
}) => {
  const filterValues: string[] = pathOr([], ['values'], rowMainFilter)

  // If the filter operator is contains, use a text input
  const valueIsStringInput: boolean = operatorHasTextInput(
    pathOr(null, ['operator'], rowMainFilter)
  )

  // If the filter operator is GREATER_THAN or LESS_THAN_OR_EQUAL use a numeric input
  const valueIsNumericInput: boolean = operatorHasNumericInput(
    pathOr(null, ['operator'], rowMainFilter)
  )

  // Hour of day and geo country code filters are special cases
  const isHourOfDayFilter = equals(
    CubeFilterDimensions.HOUR_OF_DAY,
    path(['member'], rowMainFilter)
  )
  const isGeoCountryCodeFilter =
    equals(
      CubeFilterDimensions.END_USER_COUNTRY_CODE,
      path(['member'], rowMainFilter)
    ) ||
    equals(
      CubeFilterDimensions.GEO_COUNTRY_CODE,
      path(['member'], rowMainFilter)
    )
  const isLanguageCodeFilter = equals(
    CubeFilterDimensions.LANGUAGE_DETECTED,
    path(['member'], rowMainFilter)
  )

  const isPlaybookTypeFilter = equals(
    CubeFilterDimensions.PLAYBOOK_TYPE,
    path(['member'], rowMainFilter)
  )

  const isURLParamKeyValue = equals(
    CubeFilterDimensions.URL_QUERY_PARAMETERS_PARAM_KEY_VALUE,
    path(['member'], rowMainFilter)
  )

  const isWordsOrPhrases = equals(
    CubeFilterDimensions.CHAT_INTERACTIONS_MESSAGE_BODY,
    path(['member'], rowMainFilter)
  )

  // Default value component is a select input
  const shouldUseSelectInput =
    !isHourOfDayFilter &&
    !isGeoCountryCodeFilter &&
    !isLanguageCodeFilter &&
    !valueIsStringInput &&
    !valueIsNumericInput &&
    !isPlaybookTypeFilter &&
    !isURLParamKeyValue &&
    !isWordsOrPhrases

  const filterDimensionMember: string = pathOr('', ['member'], rowMainFilter)

  return (
    <>
      {isWordsOrPhrases && (
        <WordsOrPhrases
          selectedValue={filterValues}
          isDisabled={isDisabled}
          onChange={onFilterChanged}
        />
      )}
      {isURLParamKeyValue && (
        <URLParametersSelect
          filterMember={filterDimensionMember}
          selectedURLParameters={filter}
          isDisabled={isDisabled}
          onChange={onFilterChanged}
        />
      )}
      {isHourOfDayFilter && (
        <HourRangeSelect
          hourOfDayRange={filterValues}
          isDisabled={isDisabled}
          onChange={(range: string[]) => onFilterChanged(range)}
        />
      )}
      {isLanguageCodeFilter && (
        <LanguageCodeSelect
          selectedLanguageCodes={filterValues}
          isDisabled={isDisabled}
          onChange={(langCodes: string[]) => onFilterChanged(langCodes)}
        />
      )}
      {isGeoCountryCodeFilter && (
        <CountryCodeSelect
          selectedGeoCountryCodes={filterValues}
          isDisabled={isDisabled}
          onChange={(geoCodes: string[]) => onFilterChanged(geoCodes)}
        />
      )}
      {isPlaybookTypeFilter && (
        <PlaybookTypeSelect
          selectedValue={filterValues}
          onChange={(options: string[]) => onFilterChanged(options)}
        />
      )}
      {valueIsStringInput && !isWordsOrPhrases && (
        <InputText
          rootProps={{
            id: 'cube-report-filter--value-select-text-input',
          }}
          label="Value select"
          placeholder="Select value..."
          hideLabel
          disabled={isDisabled}
          value={filterValues[0]}
          onChange={(event) => onFilterChanged([event.target.value ?? ''])}
        />
      )}
      {shouldUseSelectInput && (
        <Select
          multi={!enforceSingleSelectValue}
          label="Value select"
          placeholder="Select value..."
          hideLabel
          value={selectedPropertyOptions.filter((option) =>
            filterValues.includes(option.value)
          )}
          onChange={(options) => {
            const selectedOptions = (
              enforceSingleSelectValue ? [options] : options
            ) as SelectOption<string>[]

            const selectedValues = selectedOptions.map((option) => option.value)
            onFilterChanged(selectedValues)
          }}
          options={selectedPropertyOptions}
          isDisabled={isDisabled}
          rootProps={{
            id: 'cube-report-filter--value-select',
          }}
          maxMenuHeight={MAX_MENU_HEIGHT}
        />
      )}
      {valueIsNumericInput && (
        <InputNumber
          rootProps={{
            id: 'cube-report-filter--value-select-numeric-input',
          }}
          label="Value select"
          hideLabel={true}
          value={filterValues[0]}
          disabled={isDisabled}
          onChange={(value) => onFilterChanged([`${value}`])}
        />
      )}
    </>
  )
}

export type CubeReportFilterRowProps = {
  filter: Filter
  filterableEntities: Record<CubeFilterDimensions, IFilterableCubeEntity>
  onUpdateFilter: (newFilter: Filter) => void
  onDeleteFilter: () => void
}

export const CubeReportFilterRow = ({
  filter,
  filterableEntities,
  onUpdateFilter,
  onDeleteFilter,
}: CubeReportFilterRowProps) => {
  const { isKeyValue: isLogicalAndFilter, keyValueFilters } =
    isCubeFilterKeyValueType(filter)

  const rowMainFilter: Filter = isLogicalAndFilter
    ? (keyValueFilters[0] as LogicalAndFilter)
    : filter

  const selectedMember = pathOr(null, ['member'], rowMainFilter)
  const selectedFilterableEntity = !isNilOrEmpty(selectedMember)
    ? filterableEntities[selectedMember]
    : null
  const selectedMemberOperators =
    pathOr(null, ['availableOperations'], selectedFilterableEntity) ?? []
  const enforceSingleSelectValue = pathOr(
    false,
    ['enforceSingleSelect'],
    selectedFilterableEntity
  )
  const selectedMemberOptions =
    pathOr(null, ['optionList'], selectedFilterableEntity) ?? []

  const filterOperator = pathOr(null, ['operator'], rowMainFilter)

  const groupedFilterableEntities: SelectGroupedOptions[] = useMemo(
    () =>
      Object.entries(
        // group IFilterableCubeEntity[] by group property
        groupBy(
          (entity: IFilterableCubeEntity) => entity.group || '',
          Object.values(filterableEntities)
        )
      )
        // map groups to SelectGroupedOptions
        .map(([group, entities]) => ({
          label: group,
          options: entities
            .sort(
              // sort options alphabetically
              (a, b) =>
                a.member.label
                  .toString()
                  .localeCompare(b.member.label.toString())
            )
            .map((entity) => entity.member), // map IFilterableCubeEntity to SelectOption
        }))
        .sort(
          // sort groups by groupOrder
          (a, b) => groupOrder.indexOf(a.label) - groupOrder.indexOf(b.label)
        ),
    [filterableEntities]
  )

  const humanizedCubeOperatorOptions: SelectOption[] = useMemo(
    () =>
      Object.entries(HumanizedCubeOperators).map(([key, val]) => {
        return { label: val, value: key }
      }),
    []
  )

  const onMemberChange = useCallback(
    (value: string | LogicalAndFilter, defaultOperator?: CubeOperator) => {
      const { isKeyValue } = isCubeFilterKeyValueType(value as Filter)
      if (isKeyValue) {
        onUpdateFilter(value as LogicalAndFilter)
        return
      }

      const newFilter = {
        member: value,
        operator: defaultOperator, // Sets a default operator so users don't need to select it in the UI
      } as Filter

      onUpdateFilter(newFilter)
    },
    [onUpdateFilter]
  )

  const onOperationChange = useCallback(
    (value: CubeOperator) => {
      const { isKeyValue, keyValueFilters: filters } =
        isCubeFilterKeyValueType(filter)

      if (isKeyValue) {
        const updatedFilters: Filter[] = filters.map((filter) => {
          return { ...filter, operator: value } as Filter
        })
        const logicalAndFilter: LogicalAndFilter = {
          and: updatedFilters,
        }
        onUpdateFilter(logicalAndFilter as LogicalAndFilter)
        return
      }

      const newFilter = clone(filter) as BinaryFilter | UnaryFilter
      newFilter.operator = value
      if (isUnaryOperator(value)) {
        newFilter.values = []
      }
      onUpdateFilter(newFilter)
    },
    [filter, onUpdateFilter]
  )

  const onValueChange = useCallback(
    (
      values: string[] | LogicalAndFilter,
      logicalAndFilterValues?: { [key: string]: string }
    ) => {
      const { isKeyValue, keyValueFilters: filters } =
        isCubeFilterKeyValueType(filter)

      if (isKeyValue) {
        const updatedFilters: Filter[] = filters.map((filter) => {
          const member = pathOr('', ['member'], filter)
          return {
            ...filter,
            values: [
              logicalAndFilterValues ? logicalAndFilterValues[member] : '',
            ],
          } as Filter
        })
        const logicalAndFilter: LogicalAndFilter = {
          and: updatedFilters,
        }
        onUpdateFilter(logicalAndFilter as LogicalAndFilter)
        return
      }

      const newFilter = clone(filter) as BinaryFilter
      newFilter.values = values as string[]
      onUpdateFilter(newFilter)
    },
    [filter, onUpdateFilter]
  )

  return (
    <SimpleGrid columns={2} fullWidth css={{ gridTemplateColumns: '95% 5%' }}>
      <Flex fullWidth>
        <MemberSelect
          rowMainFilter={rowMainFilter}
          filterableEntities={filterableEntities}
          filterableEntityOptions={groupedFilterableEntities}
          onFilterChanged={onMemberChange}
        />
        <OperationSelect
          rowMainFilter={rowMainFilter}
          selectedMemberOperators={selectedMemberOperators}
          humanizedCubeOperatorOptions={humanizedCubeOperatorOptions}
          onFilterChanged={onOperationChange}
          isDisabled={isNilOrEmpty(selectedMember)}
        />
        <ValueSelect
          filter={filter}
          rowMainFilter={rowMainFilter}
          selectedPropertyOptions={selectedMemberOptions}
          onFilterChanged={onValueChange}
          enforceSingleSelectValue={enforceSingleSelectValue}
          isDisabled={
            isUnaryOperator(filterOperator) ||
            isNilOrEmpty(filterOperator) ||
            isNilOrEmpty(selectedMember)
          }
        />
      </Flex>
      <IconButton
        icon={faX}
        onClick={() => onDeleteFilter()}
        title="Delete filter"
        rootProps={{
          id: 'cube-report-filter--delete-filter-button',
        }}
      />
    </SimpleGrid>
  )
}
